import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import useAuth from '@/hooks/useAuth';
import useProfileComplete from '@/hooks/useProfileComplete';
import Spinner from '@/components/Spinner';
import { useBiometricGate } from '@/hooks/useBiometricGate';

interface Props {
  children: React.ReactElement;
}

const ProtectedRoute = ({ children }: Props) => {
  const { user, loading: authLoading } = useAuth();
  const { isComplete, loading: profileLoading } = useProfileComplete();
  const location = useLocation();
  const { ensureBiometricGate } = useBiometricGate();
  const [bioChecked, setBioChecked] = useState(false);
  const [bioOk, setBioOk] = useState(false);
  const localComplete = (() => {
    try { return localStorage.getItem('giftmap_profile_complete') === 'true'; } catch { return false; }
  })();

  useEffect(() => {
    let active = true;
    const run = async () => {
      if (authLoading || profileLoading) return;
      if (!user) return; // handled below
      console.log('[ProtectedRoute] user loaded', { userId: user?.id, path: location.pathname, isComplete, localComplete });
      // Only prompt if profile is complete (confirmed or local flag)
      if (isComplete || localComplete) {
        console.log('[ProtectedRoute] triggering biometric gate');
        const ok = await ensureBiometricGate();
        if (!active) return;
        setBioOk(ok);
        setBioChecked(true);
        console.log('[ProtectedRoute] biometric result', ok);
      } else {
        console.log('[ProtectedRoute] profile incomplete; skipping biometric');
        setBioChecked(true);
        setBioOk(true);
      }
    };
    run();
    return () => { active = false; };
  }, [user, isComplete, localComplete, authLoading, profileLoading, ensureBiometricGate]);

  if (authLoading || profileLoading || !bioChecked) {
    console.log('[ProtectedRoute] waiting', { authLoading, profileLoading, bioChecked });
    return <Spinner />;
  }

  if (!user) {
    console.log('[ProtectedRoute] redirect -> /auth');
    return <Navigate to="/auth" replace />;
  }

  // Redirect only after loading finished and confirmed incomplete
  // If localStorage says complete, allow access to prevent false positives
  if (!isComplete && !localComplete && location.pathname !== '/profile-setup') {
    console.log('[ProtectedRoute] redirect -> /profile-setup');
    return <Navigate to="/profile-setup" replace />;
  }

  // If biometric failed/cancelled, send to auth screen
  if (!bioOk) {
    console.log('[ProtectedRoute] biometric failed; redirect -> /auth');
    return <Navigate to="/auth" replace />;
  }

  return children;
};

export default ProtectedRoute;
